import { useEffect, useState } from "react";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { v4 as uuidv4 } from "uuid";
import { Box, Button, Spinner, Image } from "@/ui";
import { absoluteCenter } from "@/theme/utils/style";
import { t } from "@/utils";
import { useControls } from "@/hooks";
import NavigationBar from "@/components/NavigationBar";
import { useDetectionZones } from "./useDetectionZones";
import { useDetectionZonesDrawings } from "./useDetectionZonesDrawings";
import ControlButtons from "./ControlButtons";
import DiscardDialog from "./DiscardDialog";
import GenericError from "@/components/GenericError";
import { useDisableNavigationEvents } from "@/hooks";

export default function DetectionZones({
  cameraSettings,
  Progress,
  state,
  onClose,
  push,
  updateSettings,
  getDetectionZonesPreview
}: CSTypes.CameraSettingsStepProps) {
  const { isOpen, open, close } = useControls();
  const preservedDetectionZonesState = state.detectionZonesState;
  const [closeOnDiscard, setCloseOnDiscard] = useState(false);
  const { viewerGridMask, cameraGridMask, isLoading, preview, previewSrc, error } = useDetectionZones({
    cameraSettings,
    initialState: preservedDetectionZonesState,
    getDetectionZonesPreview
  });

  useDisableNavigationEvents(false);

  const backToMotionDetection = () => {
    push("motion-detection", { stepDirection: "previous", stateUpdate: { detectionZonesState: null } });
  };

  const handleModalPreventedClose = (reason: ModalCloseSource) => {
    if (hasUnsavedChanges) {
      setCloseOnDiscard(reason === "overlay");
      open();
    } else {
      if (reason === "esc") backToMotionDetection();
      else onClose();
    }
  };

  useEffect(() => {
    return dataSyncEmitter.on("modal-prevented-close", handleModalPreventedClose);
  });

  const {
    renderedGridRef,
    renderedGrid,
    cellDimensions,
    drawMode,
    hasUnsavedChanges,
    getActiveIndexes,
    setDrawMode,
    clearGrid,
    handleClick,
    startDrag
  } = useDetectionZonesDrawings({
    viewerGridMask,
    cameraGridMask,
    initialState: preservedDetectionZonesState
  });

  const handleSaveGrid = async () => {
    if (!viewerGridMask || !cameraGridMask) return;
    const activeIndexes = getActiveIndexes();
    const previousId = cameraSettings.settings.motionDetectionZone?.id;
    const { rows, cols } = cameraGridMask;
    const hasSelectedZones = activeIndexes.length > 0;
    await updateSettings({
      settings: {
        motionDetectionZone: hasSelectedZones
          ? {
              changeId: uuidv4(),
              id: previousId || uuidv4(),
              gridMask: {
                bits: activeIndexes,
                rows,
                cols
              }
            }
          : null
      }
    });
    backToMotionDetection();
  };

  const handleBack = () => {
    setCloseOnDiscard(false);
    if (hasUnsavedChanges) {
      open();
      return;
    }
    backToMotionDetection();
  };

  if (error)
    return (
      <GenericError
        isOpen
        onClose={() => {
          push("motion-detection", { stepDirection: "previous" });
        }}
      />
    );

  return (
    <Box>
      {Progress && <Progress />}
      <NavigationBar
        title={t("detectionZone")}
        onBack={handleBack}
        props={{ mb: "8", justifyContent: "space-between" }}
      >
        <Button onClick={handleSaveGrid} variant="filledFixedSmall" isDisabled={isLoading || !viewerGridMask}>
          {t("buttons.save")}
        </Button>
      </NavigationBar>
      {isLoading && <Spinner {...absoluteCenter} />}
      <Box width="100%" maxH="60vh" display="flex" alignItems="center" justifyContent="center">
        {previewSrc && viewerGridMask && (
          <Box position="relative" display="inline-block" ref={renderedGridRef} borderRadius="1rem" overflow="hidden">
            <Image
              src={previewSrc}
              alt="Detection zones preview"
              maxHeight="60vh"
              width="auto"
              objectFit="contain"
              userSelect="none"
              display="block"
              style={{ pointerEvents: "none" }}
            />
            {renderedGrid.map((cell, index) => {
              const row = Math.floor(index / viewerGridMask.cols);
              const col = index % viewerGridMask.cols;
              const { cellWidth, cellHeight } = cellDimensions;
              return (
                <Box
                  key={index}
                  position="absolute"
                  left={`${col * cellWidth}%`}
                  top={`${row * cellHeight}%`}
                  width={`${cellWidth}%`}
                  height={`${cellHeight}%`}
                  backgroundColor={cell.isActive ? "#10C18B66" : "transparent"}
                  border="1px solid #10C18B33"
                  boxSizing="border-box"
                  cursor="pointer"
                  onClick={() => handleClick(index)}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    startDrag(index);
                  }}
                  {...(drawMode === "draw" && {
                    _hover: {
                      backgroundColor: "#10C18B66"
                    }
                  })}
                  transition="background-color 0.1s"
                  userSelect="none"
                  style={{ touchAction: "none" }}
                />
              );
            })}
          </Box>
        )}
      </Box>
      {!isLoading && (
        <ControlButtons
          drawMode={drawMode}
          setDrawMode={setDrawMode}
          onDelete={clearGrid}
          openHelp={() =>
            push("detection-zones-help", {
              stepDirection: "next",
              stateUpdate: {
                detectionZonesState: {
                  viewerGridMask,
                  cameraGridMask,
                  preview,
                  activeIndexes: getActiveIndexes(),
                  hasUnsavedChanges,
                  drawMode
                }
              }
            })
          }
        />
      )}
      <DiscardDialog isOpen={isOpen} onClose={close} onDiscard={closeOnDiscard ? onClose : backToMotionDetection} />
    </Box>
  );
}
